import React, { useState } from "react";
import { createPortal } from "react-dom";
import { formatInUserTimezone } from "../../utils/timezone";
import nextActivityService from "../../services/nextActivityService";

function PastDueList({ items, onClose, onChanged }) {
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [showSuspendPrompt, setShowSuspendPrompt] = useState(false);
  const [suspendRemark, setSuspendRemark] = useState("");
  const [completeRemark, setCompleteRemark] = useState("");
  const [error, setError] = useState("");
  const [undoState, setUndoState] = useState(null);

  const allSelected = items.length > 0 && selected.size === items.length;

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.next_activity_id)));
  };

  const doBulkComplete = async () => {
    if (!selected.size) return;
    setBusy(true);
    setError("");
    try {
      const ids = [...selected];
      const result = await nextActivityService.bulkComplete(
        ids,
        completeRemark.trim() || null,
      );
      setUndoState({ kind: "complete", ids, affected: result?.affected || 0 });
      setSelected(new Set());
      setCompleteRemark("");
      if (onChanged) await onChanged();
    } catch (err) {
      setError(err?.error || "Failed to complete selected items");
    } finally {
      setBusy(false);
    }
  };

  const doBulkSuspend = async () => {
    if (!selected.size) return;
    if (!suspendRemark.trim()) {
      setError("A remark is required to suspend.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const ids = [...selected];
      const result = await nextActivityService.bulkSuspend(
        ids,
        suspendRemark.trim(),
      );
      setUndoState({ kind: "suspend", ids, affected: result?.affected || 0 });
      setSelected(new Set());
      setSuspendRemark("");
      setShowSuspendPrompt(false);
      if (onChanged) await onChanged();
    } catch (err) {
      setError(err?.error || "Failed to suspend selected items");
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="modal-overlay"
      role="presentation"
      onMouseDown={() => !busy && onClose()}
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        style={{ maxWidth: 720 }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>
            <i className="fas fa-exclamation-triangle" style={{ color: "#d97706", marginRight: 8 }} />
            Past Due Next Activities
          </h3>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            disabled={busy}
          >
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message" style={{ marginBottom: 10 }}>
              <i className="fas fa-exclamation-circle" /> {error}
            </div>
          )}

          <div className="past-due-toolbar">
            <label className="next-activity-check">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                disabled={busy || items.length === 0}
              />
              <span>Select all</span>
            </label>
            <span className="past-due-count">
              {selected.size} of {items.length} selected
            </span>
          </div>

          <div className="past-due-modal-list">
            {items.length === 0 ? (
              <p className="text-muted" style={{ padding: 12 }}>
                No past-due items.
              </p>
            ) : (
              items.map((item) => (
                <label
                  key={item.next_activity_id}
                  className={`past-due-modal-row ${
                    selected.has(item.next_activity_id) ? "selected" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(item.next_activity_id)}
                    onChange={() => toggle(item.next_activity_id)}
                    disabled={busy}
                  />
                  <div className="past-due-modal-info">
                    <div className="past-due-modal-line-1">
                      <strong>{item.activity_type}</strong>
                      {" · "}
                      {item.lead_company || "Lead"}
                    </div>
                    <div className="past-due-modal-line-2">
                      <i className="fas fa-clock" />{" "}
                      {formatInUserTimezone(item.planned_date)}
                      {item.notes ? ` · "${item.notes}"` : ""}
                    </div>
                  </div>
                </label>
              ))
            )}
          </div>

          {!showSuspendPrompt ? (
            <div className="past-due-remark-row">
              <label>Remark (optional for complete)</label>
              <input
                type="text"
                className="form-control"
                value={completeRemark}
                onChange={(e) => setCompleteRemark(e.target.value)}
                placeholder="Applies to all selected on complete"
                disabled={busy}
              />
            </div>
          ) : (
            <div className="past-due-remark-row">
              <label>
                Suspend reason <span className="required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={suspendRemark}
                onChange={(e) => setSuspendRemark(e.target.value)}
                placeholder="Applies to all selected"
                disabled={busy}
              />
            </div>
          )}
        </div>

        <div
          className="modal-footer"
          style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
        >
          {!showSuspendPrompt ? (
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={busy}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowSuspendPrompt(true)}
                disabled={busy || selected.size === 0}
              >
                <i className="fas fa-pause" /> Suspend Selected
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={doBulkComplete}
                disabled={busy || selected.size === 0}
              >
                {busy ? (
                  <>
                    <i className="fas fa-spinner fa-spin" /> Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check" /> Mark Selected Complete
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowSuspendPrompt(false);
                  setSuspendRemark("");
                }}
                disabled={busy}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={doBulkSuspend}
                disabled={busy || !suspendRemark.trim()}
              >
                {busy ? (
                  <>
                    <i className="fas fa-spinner fa-spin" /> Suspending...
                  </>
                ) : (
                  "Confirm Suspend"
                )}
              </button>
            </>
          )}
        </div>

        {undoState && (
          <div
            style={{
              position: "fixed",
              bottom: 24,
              right: 24,
              background: "#0a1a4a",
              color: "#fff",
              padding: "10px 16px",
              borderRadius: 10,
              boxShadow: "0 8px 25px rgba(10,26,74,0.25)",
              zIndex: 9999,
              fontSize: 13,
            }}
          >
            ✅ {undoState.affected} item(s) updated
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default PastDueList;